import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Footer } from "@/components/Footer";
import { CTABanner } from "@/components/CTABanner";
import { SEO } from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    const subject = encodeURIComponent(`Portfolio enquiry from ${data.name}`);
    const body = encodeURIComponent(`${data.message}\n\nFrom: ${data.name}\nEmail: ${data.email}`);
    window.location.href = `mailto:khaledkasmou@gmail.com?subject=${subject}&body=${body}`;
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Contact Khaled Kasmou | UX/UI Design & Research Consulting"
        description="Get in touch with Khaled Kasmou for UX/UI design, UX research, design systems, and AI-driven product design. Based in Sofia, Bulgaria — available worldwide."
        path="/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            url: "https://khaledkasmou.me/contact",
            name: "Contact Khaled Kasmou",
            mainEntity: {
              "@type": "Person",
              name: "Khaled Kasmou",
              email: "mailto:khaledkasmou@gmail.com",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Sofia",
                addressCountry: "BG",
              },
              areaServed: "Worldwide",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://khaledkasmou.me/" },
              { "@type": "ListItem", position: 2, name: "Contact", item: "https://khaledkasmou.me/contact" },
            ],
          },
        ]}
      />
      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        {/* Header */}
        <div className="mb-12 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-normal mb-4 text-foreground leading-[1.05] tracking-tight">
            {t("contact.title")}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="animate-fade-in-slow" style={{ animationDelay: "0.2s", animationFillMode: "both" }}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("contact.form.name")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("contact.form.namePlaceholder")}
                          {...field}
                          className="bg-background border-border"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("contact.form.email")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("contact.form.emailPlaceholder")}
                          {...field}
                          className="bg-background border-border"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("contact.form.message")}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t("contact.form.messagePlaceholder")}
                          className="min-h-[150px] bg-background border-border resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full gradient-primary text-primary-foreground"
                  disabled={isSubmitting}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? t("contact.form.sending") : t("contact.form.send")}
                </Button>
              </form>
            </Form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8 animate-fade-in-slow" style={{ animationDelay: "0.4s", animationFillMode: "both" }}>
            <div>
              <h2 className="text-2xl font-semibold mb-6 text-foreground">
                {t("contact.info.title")}
              </h2>

              <div className="space-y-4">
                <a
                  href="mailto:khaledkasmou@gmail.com"
                  className="flex items-center gap-3 text-foreground hover:text-muted-foreground transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("contact.info.emailLabel")}</p>
                    <p className="font-medium">khaledkasmou@gmail.com</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="border-t border-border pt-8">
              <h3 className="text-lg font-semibold mb-4 text-foreground">
                {t("contact.help.title")}
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("contact.help.li1")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("contact.help.li2")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("contact.help.li3")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t("contact.help.li4")}</span>
                </li>
              </ul>
            </div>

            <div className="border-t border-border pt-8">
              <p className="text-sm text-muted-foreground">
                {t("contact.location")}
              </p>
            </div>
          </div>
        </div>
      </main>

      <CTABanner />
      <Footer />
    </div>
  );
};

export default Contact;
